import { Node } from '@xyflow/react';
import {
    SHOPIFY_EVENT_TRIGGER_TYPE,
    shopifyEventIdFromNodeData,
    shopifyVariableFieldsFor,
} from '@/lib/automation/shopifyTriggerEvents';
import {
    WHATSAPP_ORDER_RECEIVED_TRIGGER_TYPE,
    getWhatsAppOrderEvent,
    whatsappOrderEventIdFromNodeData,
} from '@/lib/automation/whatsappOrderTriggerEvents';

export interface VariableOption {
    id: string;
    label: string;
    category: 'node' | 'contact' | 'system';
}

const VARIABLE_MAPPING_OPTIONS = [
    { label: 'Name', value: 'name', type: 'CONTACT_FIELD' },
    { label: 'Phone Number', value: 'phone_number', type: 'CONTACT_FIELD' },
    { label: 'Email', value: 'email', type: 'CONTACT_FIELD' },
];

export const getAvailableVariables = (nodes: Node[], currentNodeId?: string): VariableOption[] => {
    const vars: VariableOption[] = VARIABLE_MAPPING_OPTIONS
        .filter(o => o.type === 'CONTACT_FIELD')
        .map(o => ({
            id: `contact.${o.value}`,
            label: o.label,
            category: 'contact'
        }));

    if (!vars.find(v => v.id === 'contact.name')) {
        vars.push({ id: 'contact.name', label: 'Name', category: 'contact' });
        vars.push({ id: 'contact.phone_number', label: 'Phone Number', category: 'contact' });
        vars.push({ id: 'contact.email', label: 'Email', category: 'contact' });
    }

    vars.push({ id: 'contact.custom_fields.example', label: 'Custom Field (Example)', category: 'contact' });

    vars.push({ id: 'system.now', label: 'Current Timestamp (ISO)', category: 'system' });
    vars.push({ id: 'system.date', label: 'Current Date (YYYY-MM-DD)', category: 'system' });
    vars.push({ id: 'system.date_display', label: 'Current Date (DD/MM/YYYY)', category: 'system' });
    vars.push({ id: 'system.time', label: 'Current Time (HH:MM:SS)', category: 'system' });
    vars.push({ id: 'system.datetime', label: 'Current DateTime (Readable)', category: 'system' });
    vars.push({ id: 'system.timestamp', label: 'Unix Timestamp', category: 'system' });
    vars.push({ id: 'system.year', label: 'Current Year', category: 'system' });
    vars.push({ id: 'system.month', label: 'Current Month', category: 'system' });
    vars.push({ id: 'system.day', label: 'Current Day', category: 'system' });
    vars.push({ id: 'system.weekday', label: 'Day of Week', category: 'system' });

    vars.push({ id: 'system.date+1', label: 'Tomorrow (YYYY-MM-DD)', category: 'system' });
    vars.push({ id: 'system.date+7', label: 'Date in 7 Days', category: 'system' });
    vars.push({ id: 'system.date+30', label: 'Date in 30 Days', category: 'system' });
    vars.push({ id: 'system.date-1', label: 'Yesterday (YYYY-MM-DD)', category: 'system' });
    vars.push({ id: 'system.date-7', label: 'Date 7 Days Ago', category: 'system' });
    vars.push({ id: 'system.datetime+1', label: 'DateTime Tomorrow (Readable)', category: 'system' });
    vars.push({ id: 'system.datetime-1', label: 'DateTime Yesterday (Readable)', category: 'system' });
    vars.push({ id: 'system.now+1h', label: 'ISO Timestamp +1 Hour', category: 'system' });
    vars.push({ id: 'system.now+24h', label: 'ISO Timestamp +24 Hours', category: 'system' });
    vars.push({ id: 'system.time+1h', label: 'Time +1 Hour (HH:MM:SS)', category: 'system' });
    vars.push({ id: 'system.time+30m', label: 'Time +30 Minutes (HH:MM:SS)', category: 'system' });
    vars.push({ id: 'system.time-1h', label: 'Time -1 Hour (HH:MM:SS)', category: 'system' });
    vars.push({ id: 'system.timestamp+3600', label: 'Unix Timestamp +1 Hour', category: 'system' });
    vars.push({ id: 'system.timestamp+86400', label: 'Unix Timestamp +1 Day', category: 'system' });

    vars.push({ id: 'system.uuid', label: 'Random UUID', category: 'system' });
    vars.push({ id: 'system.random', label: 'Random Number (0-999999)', category: 'system' });
    vars.push({ id: 'system.shortid', label: 'Short Random ID (8 chars)', category: 'system' });

    vars.push({ id: 'message', label: 'Current Message Body', category: 'system' });
    vars.push({ id: 'phoneNumber', label: 'Sender Phone Number', category: 'system' });

    if (nodes?.some((n) => n.type === 'lead_received_trigger')) {
        vars.push({ id: 'lead.source', label: 'Lead source (meta)', category: 'system' });
        vars.push({ id: 'lead.name', label: 'Lead name', category: 'system' });
        vars.push({ id: 'lead.phone', label: 'Lead phone', category: 'system' });
        vars.push({ id: 'lead.email', label: 'Lead email', category: 'system' });
        vars.push({ id: 'lead.campaign_name', label: 'Lead campaign name', category: 'system' });
        vars.push({ id: 'lead.ad_name', label: 'Lead ad name', category: 'system' });
        vars.push({ id: 'lead.form_id', label: 'Lead form id', category: 'system' });
    }

    vars.push({ id: 'shopify.access_token', label: 'Shopify Access Token', category: 'system' });
    vars.push({ id: 'shopify.shop', label: 'Shopify Shop Domain', category: 'system' });
    vars.push({ id: 'shopify.api_version', label: 'Shopify API Version', category: 'system' });

    vars.push({ id: 'petpooja.app_key', label: 'PetPooja App Key', category: 'system' });
    vars.push({ id: 'petpooja.app_secret', label: 'PetPooja App Secret', category: 'system' });
    vars.push({ id: 'petpooja.access_token', label: 'PetPooja Access Token', category: 'system' });
    vars.push({ id: 'petpooja.base_url', label: 'PetPooja Base URL', category: 'system' });
    vars.push({ id: 'petpooja.restaurant_id', label: 'PetPooja Restaurant ID', category: 'system' });

    if (nodes) {
        nodes.forEach(n => {
            if (currentNodeId && n.id === currentNodeId) return;

            const nData = n.data as any;
            let baseId = '';
            let label = nData.label || n.type;

            if (nData?.variableKey) {
                baseId = nData.variableKey;
                label = `${label} (${baseId})`;
            } else if (
                n.type !== 'start' &&
                n.type !== 'starting_step' &&
                n.type !== 'note'
            ) {
                baseId = n.id;
            }

            if (baseId) {
                if (['user_input', 'ask_question'].includes(n.type)) {
                    vars.push({
                        id: baseId,
                        label: label,
                        category: 'node'
                    });
                }

                if (n.type === 'webhook' || n.type === 'http_request') {
                    const nodeLabel = (label === 'webhook' || label === 'http_request') ? 'HTTP Request' : label;
                    vars.push({ id: `${baseId}.status`, label: `${nodeLabel} Status`, category: 'system' });
                    vars.push({ id: `${baseId}.body`, label: `${nodeLabel} Body (JSON)`, category: 'system' });
                } else if (n.type === 'address') {
                    vars.push({
                        id: baseId,
                        label: `${label} (Formatted)`,
                        category: 'system'
                    });

                    ['street', 'city', 'state', 'zip', 'country', 'country_code', 'display_text', 'recipient_name', 'phone', 'landmark', 'floor', 'tower_number', 'building_name'].forEach(field => {
                        vars.push({
                            id: `${baseId}.${field}`,
                            label: `${label} ${field.charAt(0).toUpperCase() + field.slice(1).replace('_', ' ')}`,
                            category: 'system'
                        });
                    });
                } else if (n.type === 'google_sheets_trigger') {
                    vars.push({ id: `${baseId}.row_index`, label: `${label} Row Index`, category: 'system' });
                    vars.push({ id: `${baseId}.values`, label: `${label} Row Values`, category: 'system' });
                } else if (n.type === 'whatsapp_flow' || n.type === 'flow_submission') {
                    vars.push({ id: `${baseId}.data`, label: `${label} Form Data`, category: 'system' });
                    vars.push({ id: `${baseId}.screen`, label: `${label} Screen ID`, category: 'system' });
                } else if (['send_list', 'send_template', 'quick_reply', 'multiple_choice'].includes(n.type)) {
                    vars.push({ id: `${baseId}.id`, label: `${label} Selection ID`, category: 'system' });
                    vars.push({ id: `${baseId}.title`, label: `${label} Selection Title`, category: 'system' });
                } else if (['send_product', 'send_products', 'order'].includes(n.type)) {
                    vars.push({ id: `${baseId}.data.product_items`, label: `${label} Order Items`, category: 'system' });
                    vars.push({ id: `${baseId}.data.order_text`, label: `${label} Order Note`, category: 'system' });
                    vars.push({ id: `${baseId}.data.amount`, label: `${label} Order Amount`, category: 'system' });
                } else if (n.type === 'loop') {
                    const loopMode = nData.loopType || nData.loopMode || nData.config?.loopType || nData.config?.loopMode || 'count';
                    vars.push({ id: `${baseId}.currentIteration`, label: `${label} Current Iteration (1-based)`, category: 'node' });
                    vars.push({ id: `${baseId}.currentIndex`, label: `${label} Current Index (0-based)`, category: 'node' });
                    vars.push({ id: `${baseId}.maxIterations`, label: `${label} Total Iterations`, category: 'node' });
                    vars.push({ id: `${baseId}.loopCount`, label: `${label} Loop Count`, category: 'node' });
                    if (loopMode === 'iterator') {
                        vars.push({ id: `${baseId}.currentItem`, label: `${label} Current Item (full object)`, category: 'node' });
                    }
                } else if (n.type === 'data_store_get') {
                    const storeName = nData.storeName || nData.config?.storeName || 'Store';
                    vars.push({ id: `${baseId}.success`, label: `${storeName} Get Success`, category: 'node' });
                    vars.push({ id: `${baseId}.data`, label: `${storeName} Records (array)`, category: 'node' });
                    vars.push({ id: `${baseId}.total`, label: `${storeName} Total Records`, category: 'node' });
                    vars.push({ id: `${baseId}.page`, label: `${storeName} Current Page`, category: 'node' });
                    vars.push({ id: `${baseId}.per_page`, label: `${storeName} Per Page`, category: 'node' });
                    vars.push({ id: `${baseId}.total_pages`, label: `${storeName} Total Pages`, category: 'node' });
                    vars.push({ id: `${baseId}.next_page`, label: `${storeName} Next Page`, category: 'node' });
                } else if (n.type === 'data_store_create') {
                    const storeName = nData.storeName || nData.config?.storeName || 'Store';
                    vars.push({ id: `${baseId}.success`, label: `${storeName} Create Success`, category: 'node' });
                    vars.push({ id: `${baseId}.record`, label: `${storeName} Created Record`, category: 'node' });
                    vars.push({ id: `${baseId}.record.id`, label: `${storeName} Record ID`, category: 'node' });
                } else if (n.type === 'data_store_update') {
                    const storeName = nData.storeName || nData.config?.storeName || 'Store';
                    vars.push({ id: `${baseId}.success`, label: `${storeName} Update Success`, category: 'node' });
                    vars.push({ id: `${baseId}.affected`, label: `${storeName} Records Affected`, category: 'node' });
                    vars.push({ id: `${baseId}.message`, label: `${storeName} Update Message`, category: 'node' });
                } else if (n.type === 'data_store_delete') {
                    const storeName = nData.storeName || nData.config?.storeName || 'Store';
                    vars.push({ id: `${baseId}.success`, label: `${storeName} Delete Success`, category: 'node' });
                    vars.push({ id: `${baseId}.affected`, label: `${storeName} Records Deleted`, category: 'node' });
                    vars.push({ id: `${baseId}.message`, label: `${storeName} Delete Message`, category: 'node' });
                } else if (n.type === 'get_appointments') {
                    vars.push({ id: `${baseId}.slots`, label: `${label} Slots (array)`, category: 'node' });
                    vars.push({ id: `${baseId}.count`, label: `${label} Slot Count`, category: 'node' });
                    vars.push({ id: `${baseId}.date`, label: `${label} Date`, category: 'node' });
                    vars.push({ id: `${baseId}.eventTypeName`, label: `${label} Event Type Name`, category: 'node' });
                } else if (n.type === 'book_appointment') {
                    vars.push({ id: `${baseId}.success`, label: `${label} Success (true/false)`, category: 'node' });
                    vars.push({ id: `${baseId}.id`, label: `${label} Booking ID`, category: 'node' });
                    vars.push({ id: `${baseId}.start_time`, label: `${label} Start Time`, category: 'node' });
                    vars.push({ id: `${baseId}.end_time`, label: `${label} End Time`, category: 'node' });
                    vars.push({ id: `${baseId}.meeting_url`, label: `${label} Meeting URL`, category: 'node' });
                    vars.push({ id: `${baseId}.status`, label: `${label} Booking Status`, category: 'node' });
                    vars.push({ id: `${baseId}.error`, label: `${label} Error Message`, category: 'node' });
                } else if (n.type === 'get_bookings') {
                    vars.push({ id: `${baseId}.bookings`, label: `${label} Bookings (array)`, category: 'node' });
                    vars.push({ id: `${baseId}.count`, label: `${label} Booking Count`, category: 'node' });
                } else if (n.type === 'collect_payment') {
                    vars.push({ id: `${baseId}.amount`, label: `${label} Amount`, category: 'system' });
                    vars.push({ id: `${baseId}.status`, label: `${label} Status`, category: 'system' });
                    vars.push({ id: `${baseId}.payment_link`, label: `${label} Link`, category: 'system' });
                    vars.push({ id: `${baseId}.reference_id`, label: `${label} Ref ID`, category: 'system' });
                    vars.push({ id: `${baseId}.subscription_id`, label: `${label} Subscription ID`, category: 'system' });
                    vars.push({ id: `${baseId}.subscription_link`, label: `${label} Subscription Link`, category: 'system' });
                    vars.push({ id: `${baseId}.plan_id`, label: `${label} Plan ID`, category: 'system' });
                    if (nData?.applyOffers === true) {
                        vars.push({ id: `${baseId}.discount_total`, label: `${label} Discount`, category: 'system' });
                        vars.push({ id: `${baseId}.coupon_code`, label: `${label} Coupon Applied`, category: 'system' });
                        vars.push({ id: `${baseId}.coupon_rejected_reason`, label: `${label} Coupon Rejected Reason`, category: 'system' });
                    }
                } else if (n.type === WHATSAPP_ORDER_RECEIVED_TRIGGER_TYPE) {
                    getWhatsAppOrderEvent(whatsappOrderEventIdFromNodeData(nData)).variableFields.forEach((field) => {
                        vars.push({
                            id: field.path ? `${baseId}.${field.path}` : baseId,
                            label: `${label} ${field.label}`,
                            category: 'system',
                        });
                    });
                } else if (n.type === SHOPIFY_EVENT_TRIGGER_TYPE) {
                    shopifyVariableFieldsFor(shopifyEventIdFromNodeData(nData)).forEach((field) => {
                        vars.push({
                            id: field.path ? `${baseId}.${field.path}` : baseId,
                            label: `${label} ${field.label}`,
                            category: 'system',
                        });
                    });
                } else if (
                    n.type === 'shopify_trigger' ||
                    n.type === 'shopify_order_created_trigger' ||
                    n.type === 'shopify_order_updated_trigger' ||
                    n.type === 'shopify_order_paid_trigger' ||
                    n.type === 'shopify_order_cancelled_trigger'
                ) {
                    vars.push({ id: baseId, label: `${label} (Formatted)`, category: 'system' });


                    vars.push({ id: `${baseId}.orderDetails.name`, label: `${label} Order Name`, category: 'system' });
                    vars.push({ id: `${baseId}.orderDetails.id`, label: `${label} Order ID`, category: 'system' });
                    vars.push({ id: `${baseId}.orderDetails.total_price`, label: `${label} Order Price`, category: 'system' });
                    vars.push({ id: `${baseId}.orderDetails.currency`, label: `${label} Order Currency`, category: 'system' });
                    vars.push({ id: `${baseId}.orderDetails.status`, label: `${label} Order Status`, category: 'system' });
                    vars.push({ id: `${baseId}.orderDetails.financial_status`, label: `${label} Payment Status`, category: 'system' });
                    vars.push({ id: `${baseId}.orderDetails.fulfillment_status`, label: `${label} Fulfillment Status`, category: 'system' });
                    vars.push({ id: `${baseId}.orderDetails.note`, label: `${label} Order Note`, category: 'system' });
                    vars.push({ id: `${baseId}.orderDetails.order_status_url`, label: `${label} Order Link`, category: 'system' });

                    if (n.type === 'shopify_order_cancelled_trigger') {
                        vars.push({ id: `${baseId}.cancelReason`, label: `${label} Cancel Reason`, category: 'system' });
                    }

                    vars.push({ id: `${baseId}.orderDetails.customer.first_name`, label: `${label} Customer First Name`, category: 'system' });
                    vars.push({ id: `${baseId}.orderDetails.customer.last_name`, label: `${label} Customer Last Name`, category: 'system' });
                    vars.push({ id: `${baseId}.orderDetails.email`, label: `${label} Customer Email`, category: 'system' });
                    vars.push({ id: `${baseId}.orderDetails.customer.phone`, label: `${label} Customer Phone`, category: 'system' });
                } else if (n.type === 'shopify_abandoned_cart_trigger') {
                    vars.push({ id: baseId, label: `${label} (Formatted)`, category: 'system' });


                    vars.push({ id: `${baseId}.checkoutDetails.abandoned_checkout_url`, label: `${label} Recovery Link`, category: 'system' });
                    vars.push({ id: `${baseId}.checkoutDetails.total_price`, label: `${label} Cart Total`, category: 'system' });

                    vars.push({ id: `${baseId}.checkoutDetails.customer.first_name`, label: `${label} Customer First Name`, category: 'system' });
                    vars.push({ id: `${baseId}.checkoutDetails.customer.last_name`, label: `${label} Customer Last Name`, category: 'system' });
                    vars.push({ id: `${baseId}.checkoutDetails.email`, label: `${label} Customer Email`, category: 'system' });
                    vars.push({ id: `${baseId}.checkoutDetails.customer.phone`, label: `${label} Customer Phone`, category: 'system' });
                } else if (n.type === 'shopify_refund_created_trigger') {
                    vars.push({ id: baseId, label: `${label} (Formatted)`, category: 'system' });


                    vars.push({ id: `${baseId}.refundDetails.order_id`, label: `${label} Order ID`, category: 'system' });
                    vars.push({ id: `${baseId}.refundDetails.note`, label: `${label} Refund Note`, category: 'system' });

                    vars.push({ id: `${baseId}.orderDetails.name`, label: `${label} Order Name`, category: 'system' });
                    vars.push({ id: `${baseId}.orderDetails.total_price`, label: `${label} Order Price`, category: 'system' });

                    vars.push({ id: `${baseId}.orderDetails.customer.first_name`, label: `${label} Customer First Name`, category: 'system' });
                    vars.push({ id: `${baseId}.orderDetails.customer.last_name`, label: `${label} Customer Last Name`, category: 'system' });
                    vars.push({ id: `${baseId}.orderDetails.email`, label: `${label} Customer Email`, category: 'system' });
                    vars.push({ id: `${baseId}.orderDetails.customer.phone`, label: `${label} Customer Phone`, category: 'system' });
                } else if (n.type === 'shopify_customer_created_trigger') {
                    vars.push({ id: baseId, label: `${label} (Formatted)`, category: 'system' });


                    vars.push({ id: `${baseId}.customerDetails.first_name`, label: `${label} Customer First Name`, category: 'system' });
                    vars.push({ id: `${baseId}.customerDetails.last_name`, label: `${label} Customer Last Name`, category: 'system' });
                    vars.push({ id: `${baseId}.customerDetails.email`, label: `${label} Customer Email`, category: 'system' });
                    vars.push({ id: `${baseId}.customerDetails.phone`, label: `${label} Customer Phone`, category: 'system' });
                } else if (
                    n.type === 'shopify_order_shipped_trigger' ||
                    n.type === 'shopify_order_out_for_delivery_trigger' ||
                    n.type === 'shopify_order_delivered_trigger'
                ) {
                    vars.push({ id: baseId, label: `${label} (Formatted)`, category: 'system' });


                    vars.push({ id: `${baseId}.fulfillmentDetails.tracking_company`, label: `${label} Tracking Company`, category: 'system' });
                    vars.push({ id: `${baseId}.fulfillmentDetails.tracking_number`, label: `${label} Tracking Number`, category: 'system' });
                    vars.push({ id: `${baseId}.fulfillmentDetails.tracking_url`, label: `${label} Tracking URL`, category: 'system' });
                    vars.push({ id: `${baseId}.fulfillmentDetails.status`, label: `${label} Fulfillment Status`, category: 'system' });

                    vars.push({ id: `${baseId}.orderDetails.name`, label: `${label} Order Name`, category: 'system' });
                    vars.push({ id: `${baseId}.orderDetails.total_price`, label: `${label} Order Price`, category: 'system' });

                    vars.push({ id: `${baseId}.orderDetails.customer.first_name`, label: `${label} Customer First Name`, category: 'system' });
                    vars.push({ id: `${baseId}.orderDetails.customer.last_name`, label: `${label} Customer Last Name`, category: 'system' });
                    vars.push({ id: `${baseId}.orderDetails.email`, label: `${label} Customer Email`, category: 'system' });
                    vars.push({ id: `${baseId}.orderDetails.customer.phone`, label: `${label} Customer Phone`, category: 'system' });
                } else if (n.type === 'petpooja_trigger') {
                    vars.push({ id: `${baseId}.orderDetails.orderNumber`, label: `${label} Order Number`, category: 'node' });
                    vars.push({ id: `${baseId}.orderDetails.status`, label: `${label} Order Status`, category: 'node' });
                    vars.push({ id: `${baseId}.orderDetails.total`, label: `${label} Order Total`, category: 'node' });
                    vars.push({ id: `${baseId}.orderDetails.customerName`, label: `${label} Customer Name`, category: 'node' });
                    vars.push({ id: `${baseId}.orderDetails.customerPhone`, label: `${label} Customer Phone`, category: 'node' });
                    vars.push({ id: `${baseId}.orderDetails.orderId`, label: `${label} Order ID`, category: 'node' });
                } else if (n.type === 'petpooja_get_menu') {
                    vars.push({ id: `${baseId}.items`, label: `${label} Items (array)`, category: 'node' });
                    vars.push({ id: `${baseId}.menuText`, label: `${label} Menu Text`, category: 'node' });
                    vars.push({ id: `${baseId}.count`, label: `${label} Item Count`, category: 'node' });
                    vars.push({ id: `${baseId}.restaurantId`, label: `${label} Restaurant ID`, category: 'node' });
                } else if (n.type === 'petpooja_submit_order') {
                    vars.push({ id: `${baseId}.success`, label: `${label} Success`, category: 'node' });
                    vars.push({ id: `${baseId}.petpoojaOrderId`, label: `${label} PetPooja Order ID`, category: 'node' });
                    vars.push({ id: `${baseId}.localOrderId`, label: `${label} Local Order ID`, category: 'node' });
                    vars.push({ id: `${baseId}.orderStatus`, label: `${label} Order Status`, category: 'node' });
                    vars.push({ id: `${baseId}.error`, label: `${label} Error Message`, category: 'node' });
                } else if (n.type === 'petpooja_collect_order') {
                    vars.push({ id: `triggerData.paymentRequired`, label: 'Payment required? (boolean)', category: 'node' });
                    vars.push({ id: `triggerData.cartSessionId`, label: 'Cart Session ID', category: 'node' });
                    vars.push({ id: `triggerData.orderData.wenextOrderId`, label: 'WeNext Order ID', category: 'node' });
                    vars.push({ id: `triggerData.orderData.orderType`, label: 'Order Type (delivery/pickup/dine_in)', category: 'node' });
                    vars.push({ id: `triggerData.orderData.paymentMethod`, label: 'Payment Method (cod/online)', category: 'node' });
                    vars.push({ id: `triggerData.orderData.customerPhone`, label: 'Customer Phone', category: 'node' });
                    vars.push({ id: `triggerData.orderData.grandTotal`, label: 'Grand Total', category: 'node' });
                    vars.push({ id: `triggerData.orderData.items`, label: 'Items (array)', category: 'node' });
                    vars.push({ id: `triggerData.orderData.deliveryLocation.address`, label: 'Delivery Address', category: 'node' });
                    vars.push({ id: `triggerData.orderData.deliveryLocation.latitude`, label: 'Delivery Latitude', category: 'node' });
                    vars.push({ id: `triggerData.orderData.deliveryLocation.longitude`, label: 'Delivery Longitude', category: 'node' });
                } else if (!['user_input', 'ask_question'].includes(n.type)) {
                    vars.push({
                        id: baseId,
                        label: `${label} (Output)`,
                        category: 'node'
                    });
                }
            }
        });
    }

    return vars;
};
